/**
 * PropiedadSimplePanels — paneles "Identificación" y "Información para el
 * huésped" del Modal de propiedad. Extraídos juntos en round 2026-04-27
 * (Fase 8) porque ambos son chiquitos (~40-50 LOC), sin lógica
 * reactiva ni closures sobre estado del padre — solo Form.Items
 * stateless dentro del Form padre.
 *
 * Por qué los agrupamos en un mismo archivo:
 *   - Cada uno solo: <50 LOC, no justifica su propio archivo.
 *   - Patrón común: ambos son sub-secciones del mismo Modal de
 *     propiedad y solo declaran Form.Items leyéndose desde el `form`
 *     del padre vía `name=`. Cero handlers locales, cero state.
 *
 * Si en el futuro alguno crece (más campos con lógica), se puede
 * mover a su propio archivo sin tocar consumers (solo el import).
 *
 * Round 2026-05-29 — `PropiedadGuestPanel` ahora recibe `propiedadId` y
 * `modalOpen` para renderizar `GuestFilesManager` (adjuntos multimedia).
 * Sigue siendo solo presentación: la lógica de upload/delete vive en el
 * GuestFilesManager y los inputs de texto son Form.Items del padre. Ver
 * `docs/modulo-propiedades-info-huesped.md`.
 */

import React, { useMemo } from "react";
import { Alert, Divider, Form, Input, Switch, Typography } from "antd";
import GuestFilesManager from "./GuestFilesManager";
import {
  findNameMatches,
  normalizeExact,
  NameItem,
} from "../../../utils/nameSimilarity";
import { formatPropertyName } from "../../../utils/propertyDisplay";

const { Title, Text } = Typography;

// ─── PropiedadIdentityPanel ─────────────────────────────────────────────
//
// Panel "Identificación":
//  - Nombre interno de la propiedad (visible solo para el equipo, ej.
//    "Depto Papudo Vista Mar"). REQUIRED.
//  - Título de publicación (lo que el huésped ve en el listing del
//    PMS, ej. "Papudo - Primera Línea"). Opcional.
//  - Habilitada (Switch). Si la propiedad no está habilitada, no
//    aparece en el calendario admin/cliente y no recibe nuevas
//    reservas. Útil para propiedades en pausa sin querer borrarlas.

interface PropiedadIdentityPanelProps {
  /**
   * Todas las propiedades existentes (id + nombre) para detectar duplicados
   * exactos y nombres parecidos en vivo mientras el admin escribe.
   */
  existingNames: NameItem[];
  /**
   * ID de la propiedad en edición (para excluirse a sí misma). null/undefined
   * si es una creación nueva.
   */
  editingId?: number | null;
  /**
   * Nombre original de la propiedad en edición. Se usa para "grandfatherear":
   * si el admin no cambia el nombre, no bloqueamos aunque sea un duplicado
   * legacy preexistente (igual que el backend). Vacío en creación.
   */
  editingOriginalName?: string | null;
}

export const PropiedadIdentityPanel: React.FC<PropiedadIdentityPanelProps> = ({
  existingNames,
  editingId,
  editingOriginalName,
}) => {
  // `useFormInstance` toma el Form del padre (este panel se renderiza dentro
  // de <Form>); con él observamos el nombre para recalcular los parecidos.
  const form = Form.useFormInstance();
  const nombre = Form.useWatch("nombrePropiedad", form) as string | undefined;

  // Nombres parecidos (no idénticos) — advertencia no bloqueante. Los
  // idénticos se manejan en el validator del Form.Item (bloquea el submit).
  const { similar } = useMemo(
    () =>
      findNameMatches(nombre ?? "", existingNames, {
        excludeId: editingId ?? null,
      }),
    [nombre, existingNames, editingId],
  );

  return (
    <div className="flex-container">
      <Form.Item
        label="Nombre de la propiedad"
        name="nombrePropiedad"
        // El validator de duplicado exacto se integra con `validateFields()`:
        // bloquea el guardado y muestra el error inline en vivo. Trata
        // mayúsculas/minúsculas y espacios como lo mismo (ver nameSimilarity).
        rules={[
          {
            required: true,
            message: "El nombre de la propiedad es obligatorio",
          },
          {
            validator: (_rule, value) => {
              // Grandfather: si editamos y el nombre NO cambió respecto al
              // original, no bloqueamos (puede ser un duplicado legacy
              // preexistente). Igual que el backend: solo se bloquea si el
              // nombre CAMBIA a uno que choca con otra propiedad.
              if (
                editingId != null &&
                normalizeExact(value) === normalizeExact(editingOriginalName)
              ) {
                return Promise.resolve();
              }
              const { exact } = findNameMatches(value ?? "", existingNames, {
                excludeId: editingId ?? null,
              });
              if (exact.length) {
                return Promise.reject(
                  new Error(
                    `Ya existe una propiedad con este nombre (mayúsculas/minúsculas no cuentan): «${formatPropertyName(exact[0].nombrepropiedad)}». No puede haber dos iguales.`,
                  ),
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input placeholder="Ej: Depto Papudo Vista Mar" />
      </Form.Item>

      {similar.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: -8, marginBottom: 12 }}
          message="Hay nombres parecidos ya registrados"
          description={
            <>
              <div style={{ marginBottom: 4 }}>
                Estos se ven o suenan parecido. Asegúrate de que el nombre sea
                distinguible para no confundirlos:
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {similar.map((p) => (
                  <li key={p.id}>{formatPropertyName(p.nombrepropiedad)}</li>
                ))}
              </ul>
            </>
          }
        />
      )}

      <Form.Item label="Título de la publicación" name="nombrePublicacion">
        <Input placeholder="Ej: Papudo - Primera Línea" />
      </Form.Item>

      <Form.Item
        label="Habilitada"
        name="habilitado"
        valuePropName="checked"
        initialValue={true}
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label="Avisos por correo al cliente"
        name="correosClienteActivos"
        valuePropName="checked"
        initialValue={true}
        tooltip="Apagado, el cliente no recibe los correos de aviso de ESTA propiedad (cierre de mes publicado y observaciones compartidas). No afecta la recuperación de contraseña ni el correo de bienvenida. El interruptor general está en Configuración → Correos al cliente."
        extra="Para apagar los avisos de todas las propiedades a la vez: Configuración → Correos al cliente."
      >
        <Switch />
      </Form.Item>
    </div>
  );
};

// ─── PropiedadGuestPanel ────────────────────────────────────────────────
//
// Panel "Información para el huésped":
//  - Mensaje inicial: instrucciones de llegada (autocheckin, ubicación
//    de llaves, código de acceso al edificio). Se copia al portapapeles
//    desde el calendario para enviarlo por WhatsApp/email cuando el
//    huésped pregunta.
//  - WiFi: SSID + password de la propiedad. Display al huésped.
//  - Clave cerradura: código numérico de cerradura inteligente.
//  - Otros: campo libre para datos adicionales (alarma, basura, etc.).
//  - Adjuntos multimedia (round 2026-05-29): fotos, videos, PDFs y docs
//    que ilustran o complementan las instrucciones escritas — por ej.
//    foto del termostato, video del uso de la cerradura, plano del
//    estacionamiento. Renderiza `GuestFilesManager`, que necesita el
//    `propiedadId` (los archivos viven en la columna
//    `propiedades.info_huesped_archivos` y solo se pueden subir cuando
//    la propiedad ya existe en DB).
//
// TODOS los campos son opcionales — algunas propiedades no tienen
// cerradura inteligente, otras requieren entrega presencial de llaves.

interface PropiedadGuestPanelProps {
  /**
   * ID de la propiedad si estamos editando (null si es creación nueva).
   * Los adjuntos requieren id porque viven asociados a un row existente.
   */
  propiedadId: number | null | undefined;
  /**
   * Pasamos `open` del Modal padre para que el manager solo monte el
   * listener de Ctrl+V (paste) cuando el modal está visible.
   */
  modalOpen: boolean;
}

export const PropiedadGuestPanel: React.FC<PropiedadGuestPanelProps> = ({
  propiedadId,
  modalOpen,
}) => (
  <>
    <Form.Item
      label="Mensaje inicial"
      name="mensajeInicial"
      tooltip="Instrucciones de llegada para el huésped. Se copian al portapapeles desde el calendario"
    >
      <Input.TextArea
        rows={4}
        placeholder="Mensaje de bienvenida para el huésped..."
      />
    </Form.Item>

    <div className="flex-container">
      <Form.Item
        label="WiFi"
        name="claveWifi"
        tooltip="Información de la red WiFi de la propiedad para el huésped"
      >
        <Input.TextArea rows={3} placeholder="(opcional)" />
      </Form.Item>

      <Form.Item
        label="Clave cerradura"
        name="claveCerradura"
        tooltip="Código de acceso a la cerradura de la propiedad"
      >
        <Input.TextArea rows={3} placeholder="(opcional)" />
      </Form.Item>
    </div>

    <Form.Item label="Otros" name="otros">
      <Input.TextArea rows={3} />
    </Form.Item>

    {/* ─── Adjuntos multimedia (fotos / videos / PDFs / docs) ──────── */}
    <Divider style={{ marginTop: 18, marginBottom: 12 }}>
      <Title level={5} style={{ margin: 0 }}>
        Archivos para el huésped
      </Title>
    </Divider>
    <Text type="secondary" style={{ display: "block", marginBottom: 10 }}>
      Suma fotos del termostato, videos del uso de la cerradura, mapas del
      estacionamiento o cualquier documento que ayude al huésped. Se pueden
      descargar individualmente o todos juntos en un ZIP.
    </Text>
    <GuestFilesManager propiedadId={propiedadId} modalOpen={modalOpen} />
  </>
);
